import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import { CLAUDE_SONNET_4, CLAUDE_OPUS_4, CLAUDE_HAIKU_4_5 } from '@pistachiorama/shared-types';
export interface Model {
  id: string;
  name: string;
  provider: 'anthropic' | 'openai' | 'google' | 'local';
  description: string;
  speed: 'fast' | 'medium' | 'slow';
  isPro?: boolean;
  recommended?: boolean;
}
interface ModelSelectorProps {
  selectedModel: Model;
  onSelectModel: (model: Model) => void;
  compact?: boolean;
}

export type { ModelSelectorProps };
// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_MODEL: Model = {
  id: CLAUDE_SONNET_4,
  name: 'Claude Sonnet 4.6',
  provider: 'anthropic',
  description: 'Fast, great for coding',
  speed: 'fast',
  recommended: true
};

// eslint-disable-next-line react-refresh/only-export-components
export const MODELS: Model[] = [
// Anthropic
{
  id: CLAUDE_SONNET_4,
  name: 'Claude Sonnet 4.6',
  provider: 'anthropic',
  description: 'Fast, great for coding',
  speed: 'fast',
  recommended: true
}, {
  id: CLAUDE_OPUS_4,
  name: 'Claude Opus 4.6',
  provider: 'anthropic',
  description: 'Most capable, slower',
  speed: 'slow',
  isPro: true
}, {
  id: CLAUDE_HAIKU_4_5,
  name: 'Claude Haiku 4.5',
  provider: 'anthropic',
  description: 'Fastest, lightweight tasks',
  speed: 'fast'
},
// OpenAI
{
  id: 'gpt-4o',
  name: 'GPT-4o',
  provider: 'openai',
  description: 'Multimodal, fast',
  speed: 'fast'
}, {
  id: 'gpt-4-turbo',
  name: 'GPT-4 Turbo',
  provider: 'openai',
  description: 'Large context',
  speed: 'medium',
  isPro: true
}, {
  id: 'o1-preview',
  name: 'o1-preview',
  provider: 'openai',
  description: 'Complex reasoning',
  speed: 'slow',
  isPro: true
},
// Google
{
  id: 'gemini-2.5-pro',
  name: 'Gemini 2.5 Pro',
  provider: 'google',
  description: 'Advanced reasoning',
  speed: 'medium'
}, {
  id: 'gemini-2.5-flash',
  name: 'Gemini 2.5 Flash',
  provider: 'google',
  description: 'Speed optimized',
  speed: 'fast'
},
// Local
{
  id: 'llama-3.1-70b',
  name: 'Llama 3.1 70B',
  provider: 'local',
  description: 'Running locally',
  speed: 'medium'
}];
const PROVIDER_NAMES = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  local: 'Local'
};
const PROVIDER_LOGOS = {
  anthropic: '🔶',
  openai: '⚡',
  google: '🔷',
  local: '💻'
};
const getSpeedIndicator = (speed: string) => {
  switch (speed) {
    case 'fast':
      return <span className="text-success-400">🟢</span>;
    case 'medium':
      return <span className="text-warning-400">🟡</span>;
    case 'slow':
      return <span className="text-danger-400">🔴</span>;
    default:
      return null;
  }
};
export function ModelSelector({
  selectedModel,
  onSelectModel
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const groupedModels = MODELS.reduce((acc, model) => {
    if (!acc[model.provider]) acc[model.provider] = [];
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<string, Model[]>);
  return <div className="relative">
      {/* Collapsed State */}
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-800 hover:bg-surface-700 border border-surface-500 transition-colors">
        <span className="text-lg">
          {PROVIDER_LOGOS[selectedModel.provider]}
        </span>
        <span className="text-sm text-text-primary">{selectedModel.name}</span>
        <ChevronDown className={`w-3 h-3 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div initial={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} transition={{
          duration: 0.15
        }} className="absolute top-full left-0 mt-2 w-80 bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50 overflow-hidden">
              <div className="p-3 border-b border-surface-600">
                <h3 className="text-sm font-semibold text-text-primary">
                  Select Model
                </h3>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {Object.entries(groupedModels).map(([provider, models]) => <div key={provider} className="border-b border-surface-600 last:border-0">
                    <div className="px-3 py-2 bg-surface-800/50">
                      <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">
                        {PROVIDER_NAMES[provider as keyof typeof PROVIDER_NAMES]}
                      </span>
                    </div>
                    <div className="p-1">
                      {models.map(model => <button key={model.id} onClick={() => {
                  onSelectModel(model);
                  setIsOpen(false);
                }} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-surface-800 transition-colors group">
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-lg">
                              {PROVIDER_LOGOS[model.provider]}
                            </span>
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-text-primary group-hover:text-primary-400 transition-colors">
                                  {model.name}
                                </span>
                                {model.recommended && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400 font-medium">
                                    Recommended
                                  </span>}
                                {model.isPro && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 font-medium">
                                    Pro
                                  </span>}
                              </div>
                              <div className="text-xs text-text-muted">
                                {model.description}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getSpeedIndicator(model.speed)}
                            {selectedModel.id === model.id && <Check className="w-4 h-4 text-primary-400" />}
                          </div>
                        </button>)}
                    </div>
                  </div>)}
              </div>

              <div className="p-3 border-t border-surface-600 bg-surface-900/50 flex items-center justify-between text-xs">
                <button className="text-primary-400 hover:text-primary-300">
                  Compare models
                </button>
                <button className="text-text-muted hover:text-text-secondary">
                  Configure local
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}